// client/src/components/servicos/ServicoForm.js - VERSÃO DEFINITIVA COM SUPORTE A POOL
import React, { useState, useEffect, useCallback } from 'react';
import { 
    XCircle, Loader2, PlusCircle, Trash2, Globe,
    Building, Cpu, MapPin, Clock, Package, Tag,
    Minus, ChevronDown, Layers, Link as LinkIcon
} from 'lucide-react';
import useAlert from '../../hooks/useAlert';
import { useMutateServicos } from '../../hooks/useServicos';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency, getCurrencySymbol } from '../../utils/formatters';
import PropTypes from 'prop-types';
import { FormStepper, FormStepNavigation } from '../shared/FormStepper';

const DEFAULT_PLACEHOLDER_IMAGE = '/default-recurso.png';
const STEP_IDS = ['basicos', 'preco', 'unidades', 'vinculos', 'configuracoes'];
const STEP_LABELS = ['Dados Básicos', 'Preço', 'Unidades', 'Vínculos', 'Configurações'];

const extractId = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    if (typeof item === 'object') {
        if (item.$oid) return String(item.$oid);
        if (item._id) return String(item._id);
        if (item.id) return String(item.id);
    }
    return String(item);
};

const initialState = {
  nome: '',
  descricao: '',
  categoria: '',
  ativo: true,
  agendavel: false,
  duracaoSessaoEmMinutos: 30,
  controlaSessao: false,
  recursosVinculados: [],
  // ✅ NOVOS CAMPOS: Pool de Recursos
  usaPoolRecurso: false,
  poolRecursoId: '',
  quantidadeRecursosNecessaria: 1,
  recursosObrigatorios: [],
  imagensDaGaleria: [],
  imagens: [],
  imagemPrincipal: null,
  imagemUrl: '',
  visivelParaCliente: true,
  precoVisivelParaCliente: true,
  tipoDePrecificacao: 'Fixo',
  valorFixo: 0,
  pacotesDePreco: [],
  requerAnamnese: false,
  configAnamnese: { 
    templateAnamnese: null, 
    frequencia: 'Não Repetir',
    obrigatoria: true,
    diasValidade: 30
  },
  metadata: {
    tipoServico: 'interno',
    categoriaItem: 'servico',
    disponivelOnline: false,
    tempoPreparacao: 0,
    tempoLimpeza: 0,
    requerDeslocamento: false,
    raioAtendimento: 0,
    tempoDeslocamento: 0,
    custoDeslocamento: 0,
    nivelComplexidade: 'baixa',
    restricoes: [],
    instrucoesPreparacao: '',
    observacoesInternas: ''
  },
  unidadesDetalhes: [],
  status: 'Ativo',
  tags: []
};

const ServicoForm = ({
    onCloseForm,
    current,
    onCreateSuccess,
    accountId,
    recursos = [],
    pools = [],
    unidades = [],
    categorias = [],
    isLoadingRecursos = false,
    isErrorRecursos = false,
    isLoadingPools = false,
    isErrorPools = false,
    isLoadingUnidades = false,
    isErrorUnidades = false,
    isLoadingCategorias = false,
    isErrorCategorias = false,
    mode = 'create'
}) => {
    const { setAlert } = useAlert();
    // ✅ ACHADO: rótulos/valores abaixo eram fixos em BRL/"(R$)", sem ler
    // configuracoesGerais.i18n da conta.
    const { currency, locale } = useTenantI18n();
    const currencySymbol = getCurrencySymbol(currency, locale);
    const servicoMutations = useMutateServicos(accountId);
    
    const addServico = servicoMutations?.createServico || {};
    const updateServico = servicoMutations?.updateServico || {};
    const validateServicoPrecificacao = servicoMutations?.validateServicoPrecificacao || {};

    const [servico, setServico] = useState(initialState);
    const [currentStep, setCurrentStep] = useState(1);
    const goToTab = (tabId) => {
        const index = STEP_IDS.indexOf(tabId);
        if (index >= 0) setCurrentStep(index + 1);
    };
    const [errors, setErrors] = useState({});
    const [validating, setValidating] = useState(false);
    const [dirtyFields, setDirtyFields] = useState(new Set());
    const [isInitialized, setIsInitialized] = useState(false);
    const [novaTag, setNovaTag] = useState('');
    const [expandedUnidades, setExpandedUnidades] = useState({});

    const isEditing = mode === 'edit';
    const loading = addServico?.isPending || updateServico?.isPending || false;
    const isServicoExterno = servico.metadata?.tipoServico === 'externo';
    const hasUsoExterno = isServicoExterno;

    useEffect(() => {
        if (mode === 'edit' && current && !isInitialized) {
            let categoriaProcessada = extractId(current.categoria);
            const dadosProcessados = {
                ...initialState,
                ...current,
                valorFixo: current.valorFixo !== undefined && current.valorFixo !== null ? Number(current.valorFixo) : 0,
                duracaoSessaoEmMinutos: current.duracaoSessaoEmMinutos !== undefined && current.duracaoSessaoEmMinutos !== null ? Number(current.duracaoSessaoEmMinutos) : 30,
                tipoDePrecificacao: current.tipoDePrecificacao || 'Fixo',
                status: current.ativo ? 'Ativo' : 'Inativo',
                imagemUrl: current.imagemUrl || '',
                // ✅ NOVOS CAMPOS
                usaPoolRecurso: current.usaPoolRecurso || false,
                poolRecursoId: extractId(current.poolRecursoId) || '',
                quantidadeRecursosNecessaria: current.quantidadeRecursosNecessaria || 1,
                recursosObrigatorios: Array.isArray(current.recursosObrigatorios) ? current.recursosObrigatorios.map(r => extractId(r)) : [],
                metadata: {
                    tipoServico: current.metadata?.tipoServico || 'interno',
                    categoriaItem: current.metadata?.categoriaItem || 'servico',
                    disponivelOnline: current.metadata?.disponivelOnline || false,
                    tempoPreparacao: current.metadata?.tempoPreparacao || 0,
                    tempoLimpeza: current.metadata?.tempoLimpeza || 0,
                    requerDeslocamento: current.metadata?.requerDeslocamento || false,
                    raioAtendimento: current.metadata?.raioAtendimento || 0,
                    tempoDeslocamento: current.metadata?.tempoDeslocamento || 0,
                    custoDeslocamento: current.metadata?.custoDeslocamento || 0,
                    nivelComplexidade: current.metadata?.nivelComplexidade || 'baixa',
                    restricoes: Array.isArray(current.metadata?.restricoes) ? current.metadata.restricoes : [],
                    instrucoesPreparacao: current.metadata?.instrucoesPreparacao || '',
                    observacoesInternas: current.metadata?.observacoesInternas || '',
                    ...current.metadata
                },
                categoria: categoriaProcessada || '',
                recursosVinculados: Array.isArray(current.recursosVinculados) ? current.recursosVinculados.map(r => extractId(r)) : [],
                pacotesDePreco: Array.isArray(current.pacotesDePreco) 
                    ? current.pacotesDePreco.map(pacote => ({
                        ...pacote,
                        sessoes: Number(pacote.sessoes) || 1,
                        valor: Number(pacote.valor) || 0,
                        descontoPercentual: Number(pacote.descontoPercentual) || 0,
                        validadeMeses: Number(pacote.validadeMeses) || 12
                    })) : [],
                tags: Array.isArray(current.tags) ? current.tags : [],
                unidadesDetalhes: Array.isArray(current.unidadesDetalhes) 
                    ? current.unidadesDetalhes.map(ud => ({ ...ud, unidade: extractId(ud.unidade) })) : []
            };

            setServico(dadosProcessados);
            setIsInitialized(true);
        } else if (mode === 'create' && !isInitialized) {
            // ✅ Prefill vindo do Catálogo de Modelos (ver catalogoModeloController.js
            // ::importarParaConta + ImportarCatalogoPage.js) — sem _id, então
            // continua em modo criação, só com os campos iniciais já preenchidos.
            setServico(current ? {
                ...initialState,
                ...current,
                categoria: extractId(current.categoria) || '',
                valorFixo: current.valorFixo !== undefined && current.valorFixo !== null ? Number(current.valorFixo) : initialState.valorFixo,
                duracaoSessaoEmMinutos: current.duracaoSessaoEmMinutos !== undefined && current.duracaoSessaoEmMinutos !== null ? Number(current.duracaoSessaoEmMinutos) : initialState.duracaoSessaoEmMinutos
            } : initialState);
            setIsInitialized(true);
        }
    }, [current, mode, isInitialized]);

    useEffect(() => {
        setIsInitialized(false);
    }, [mode]);

    const markFieldAsDirty = (fieldName) => {
        setDirtyFields(prev => new Set([...prev, fieldName]));
    };

    const onChange = e => {
        const { name, value, type, checked } = e.target;
        markFieldAsDirty(name);
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
        
        if (name.startsWith('metadata.')) {
            const metadataField = name.replace('metadata.', '');
            setServico(prev => ({
                ...prev,
                metadata: {
                    ...prev.metadata,
                    [metadataField]: type === 'checkbox' ? checked : 
                                 (['tempoPreparacao', 'tempoLimpeza', 'raioAtendimento', 'tempoDeslocamento', 'custoDeslocamento'].includes(metadataField) ? parseFloat(value) || 0 : value)
                }
            }));
        } else if (name.startsWith('configAnamnese.')) {
            const anamneseField = name.replace('configAnamnese.', '');
            setServico(prev => ({
                ...prev,
                configAnamnese: {
                    ...prev.configAnamnese,
                    [anamneseField]: type === 'checkbox' ? checked : (anamneseField === 'diasValidade' ? parseInt(value) || 0 : value)
                }
            }));
        } else if (name === 'valorFixo' || name === 'duracaoSessaoEmMinutos' || name === 'quantidadeRecursosNecessaria') {
            setServico(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
        } else if (type === 'checkbox') {
            setServico(prev => ({ ...prev, [name]: checked }));
        } else {
            setServico(prev => ({ ...prev, [name]: value }));
        }
    };

    const onCheckboxChange = (e) => {
        const { name, value, checked } = e.target;
        markFieldAsDirty(name);
        setServico(prev => {
            const currentArray = Array.isArray(prev[name]) ? [...prev[name]] : [];
            if (checked) {
                if (!currentArray.includes(value)) return { ...prev, [name]: [...currentArray, value] };
            } else {
                return { ...prev, [name]: currentArray.filter(id => id !== value) };
            }
            return prev;
        });
    };

    const adicionarTag = useCallback(() => {
        if (novaTag.trim() && !servico.tags.includes(novaTag.trim())) {
            setServico(prev => ({ ...prev, tags: [...prev.tags, novaTag.trim()] }));
            setNovaTag('');
            markFieldAsDirty('tags');
        }
    }, [novaTag, servico.tags]);

    const removerTag = useCallback((tagParaRemover) => {
        setServico(prev => ({ ...prev, tags: prev.tags.filter(tag => tag !== tagParaRemover) }));
        markFieldAsDirty('tags');
    }, []);

    const handleTagKeyPress = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            adicionarTag();
        }
    }, [adicionarTag]);

    const toggleUnidadeExpansion = (unidadeId) => {
        setExpandedUnidades(prev => ({
            ...prev,
            [unidadeId]: !prev[unidadeId]
        }));
    };

    const onUnidadeChange = (unidadeId, checked) => {
        markFieldAsDirty('unidadesDetalhes');
        
        setServico(prev => {
            let novasUnidades = [...prev.unidadesDetalhes];
            
            if (checked) {
                if (!novasUnidades.find(ud => ud.unidade === unidadeId)) {
                    novasUnidades.push({
                        unidade: unidadeId,
                        ativo: true,
                        precoPersonalizado: null,
                        recursosVinculados: [],
                        tempoPreparacao: null,
                        tempoLimpeza: null,
                        disponivelOnline: prev.metadata.disponivelOnline,
                        observacoes: ''
                    });
                }
                setExpandedUnidades(prevExp => ({ ...prevExp, [unidadeId]: true }));
            } else {
                novasUnidades = novasUnidades.filter(ud => ud.unidade !== unidadeId);
            }
            
            return { ...prev, unidadesDetalhes: novasUnidades };
        });
    };

    const onUnidadeConfigChange = (unidadeId, field, value) => {
        markFieldAsDirty('unidadesDetalhes');
        setServico(prev => ({
            ...prev,
            unidadesDetalhes: prev.unidadesDetalhes.map(ud => ud.unidade === unidadeId ? { ...ud, [field]: value } : ud)
        }));
    };

    const handleAddPacote = () => {
        markFieldAsDirty('pacotesDePreco');
        setServico(prev => ({
            ...prev,
            pacotesDePreco: [...prev.pacotesDePreco, { nome: `Pacote ${prev.pacotesDePreco.length + 1}`, sessoes: 1, valor: 0, descontoPercentual: 0, validadeMeses: 12, ativo: true, descricao: '' }]
        }));
    };

    const handleRemovePacote = (index) => {
        markFieldAsDirty('pacotesDePreco');
        setServico(prev => ({ ...prev, pacotesDePreco: prev.pacotesDePreco.filter((_, i) => i !== index) }));
    };

    const handlePacoteChange = (index, field, value) => {
        markFieldAsDirty('pacotesDePreco');
        let processedValue = value;
        if (['sessoes', 'valor', 'descontoPercentual', 'validadeMeses'].includes(field)) {
            processedValue = field === 'descontoPercentual' ? Math.min(100, Math.max(0, parseFloat(value) || 0)) : parseFloat(value) || 0;
            if (field === 'sessoes' && processedValue < 1) processedValue = 1;
            if (field === 'valor' && processedValue <= 0) processedValue = 0;
        }
        setServico(prev => ({ ...prev, pacotesDePreco: prev.pacotesDePreco.map((pacote, i) => i === index ? { ...pacote, [field]: processedValue } : pacote) }));
    };

    const togglePacoteAtivo = (index) => {
        markFieldAsDirty('pacotesDePreco');
        setServico(prev => ({ ...prev, pacotesDePreco: prev.pacotesDePreco.map((pacote, i) => i === index ? { ...pacote, ativo: !pacote.ativo } : pacote) }));
    };

    const validarCamposObrigatorios = () => {
        const novosErros = {};
        if (!servico.nome || servico.nome.trim() === '') novosErros.nome = 'Nome do serviço é obrigatório';
        if (servico.agendavel && (!servico.duracaoSessaoEmMinutos || servico.duracaoSessaoEmMinutos <= 0)) novosErros.duracaoSessaoEmMinutos = 'Duração é obrigatória para serviços agendáveis';
        // ✅ VALIDAÇÃO DE POOL
        if (servico.usaPoolRecurso && !servico.poolRecursoId) {
            novosErros.poolRecursoId = 'Selecione um pool de recursos';
        }
        if (servico.usaPoolRecurso && servico.recursosObrigatorios && servico.recursosObrigatorios.length > 0) {
            novosErros.recursosObrigatorios = 'Não é permitido ter pool E recursos fixos simultaneamente';
        }
        return novosErros;
    };

    const validarPacotes = () => {
        const novosErros = {};
        if (servico.tipoDePrecificacao === 'Por Pacote') {
            if (!servico.pacotesDePreco || servico.pacotesDePreco.length === 0) return { pacotes: 'Serviços com precificação por pacote devem ter pelo menos um pacote configurado.' };
            servico.pacotesDePreco.forEach((pacote, index) => {
                if (!pacote.nome || !pacote.nome.trim()) novosErros[`pacote-${index}-nome`] = 'Nome do pacote é obrigatório';
                if (!pacote.sessoes || pacote.sessoes <= 0) novosErros[`pacote-${index}-sessoes`] = 'Número de sessões deve ser maior que zero';
                if (!pacote.valor || pacote.valor <= 0) novosErros[`pacote-${index}-valor`] = 'Valor deve ser maior que zero';
                if (pacote.descontoPercentual !== undefined && (pacote.descontoPercentual < 0 || pacote.descontoPercentual > 100)) novosErros[`pacote-${index}-desconto`] = 'Desconto deve estar entre 0 e 100%';
                if (pacote.validadeMeses !== undefined && (pacote.validadeMeses < 1 || pacote.validadeMeses > 36)) novosErros[`pacote-${index}-validade`] = 'Validade deve estar entre 1 e 36 meses';
            });
        }
        return novosErros;
    };

    const validarServicoExterno = () => {
        const novosErros = {};
        if (isServicoExterno) {
            if (servico.metadata.requerDeslocamento) {
                if (!servico.metadata.raioAtendimento || servico.metadata.raioAtendimento <= 0) novosErros.raioAtendimento = 'Raio de atendimento é obrigatório para serviços com deslocamento';
                if (!servico.metadata.tempoDeslocamento || servico.metadata.tempoDeslocamento < 0) novosErros.tempoDeslocamento = 'Tempo de deslocamento é obrigatório';
                if (servico.metadata.custoDeslocamento === undefined || servico.metadata.custoDeslocamento < 0) novosErros.custoDeslocamento = 'Custo de deslocamento é obrigatório';
            }
        }
        return novosErros;
    };

    const validarCategoria = () => {
        const novosErros = {};
        if (!servico.categoria || servico.categoria.trim() === '') {
            novosErros.categoria = 'Categoria é obrigatória';
        } else {
            const categoriaExiste = categorias.some(cat => extractId(cat) === servico.categoria);
            if (!categoriaExiste) novosErros.categoria = 'Por favor, selecione uma categoria válida da lista';
        }
        return novosErros;
    };

    const getFieldError = (fieldName) => errors[fieldName];

    const validarPrecificacao = async (servicoData) => {
        try {
            setValidating(true);
            await validateServicoPrecificacao.mutateAsync(servicoData);
            return true;
        } catch (error) {
            setAlert(error.response?.data?.error || 'Erro na validação da precificação', 'danger');
            return false;
        } finally {
            setValidating(false);
        }
    };

    const calcularDuracaoTotal = () => {
        const duracaoBase = servico.duracaoSessaoEmMinutos || 0;
        const preparacao = servico.metadata.tempoPreparacao || 0;
        const limpeza = servico.metadata.tempoLimpeza || 0;
        const deslocamento = isServicoExterno ? (servico.metadata.tempoDeslocamento || 0) : 0;
        return duracaoBase + preparacao + limpeza + deslocamento;
    };

    const isUnidadeVinculada = (unidadeId) => servico.unidadesDetalhes.some(ud => ud.unidade === unidadeId);
    const getUnidadeDetalhes = (unidadeId) => servico.unidadesDetalhes.find(ud => ud.unidade === unidadeId);

    // ==========================================
    // FUNÇÕES DE RENDERIZAÇÃO
    // ==========================================

    const renderCategoriaField = () => {
        if (isLoadingCategorias) return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 size={16} className="animate-spin" /> Carregando categorias...</div>;
        if (isErrorCategorias) return <div className="text-red-500 text-sm">Erro ao carregar categorias</div>;
        return (
            <>
                <select id="categoria" name="categoria" value={servico.categoria || ''} onChange={onChange} className={`form-input ${errors.categoria ? 'border-red-500' : ''}`} required>
                    <option value="">Selecione uma categoria</option>
                    {categorias.length > 0 ? (
                        categorias.map(categoria => (
                            <option key={extractId(categoria)} value={extractId(categoria)}>{categoria.nome}</option>
                        ))
                    ) : (
                        <option disabled value="">Nenhuma categoria disponível</option>
                    )}
                </select>
                {errors.categoria && <p className="text-red-500 text-xs mt-1">{errors.categoria}</p>}
            </>
        );
    };

    // ✅ NOVA FUNÇÃO: Renderizar seção de Pool de Recursos
    const renderSeçãoPoolRecursos = () => {
        return (
            <div className="form-group full-width mb-6">
                <label className="form-group-title flex items-center gap-2 text-lg font-semibold border-b pb-2">
                    <Layers size={20} className="text-gray-600" /> Estratégia de Recursos
                </label>
                <p className="form-hint mb-4 mt-2">
                    Escolha a estratégia de alocação de recursos para este serviço.
                </p>
                
                <div className="space-y-4 border p-4 rounded-lg bg-gray-50">
                    {/* Opção 1: Recursos Fixos Obrigatórios */}
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-white border border-gray-200">
                        <input 
                            type="radio" 
                            id="estrategia-fixa" 
                            name="usaPoolRecurso" 
                            value={false} 
                            checked={!servico.usaPoolRecurso} 
                            onChange={(e) => {
                                setServico(prev => ({ ...prev, usaPoolRecurso: false, poolRecursoId: '', recursosObrigatorios: prev.recursosObrigatorios || [] }));
                            }} 
                            className="form-radio mt-1"
                        />
                        <div className="flex-1">
                            <label htmlFor="estrategia-fixa" className="font-medium flex items-center gap-2">
                                <LinkIcon size={16} className="text-blue-600" /> Recursos Fixos Obrigatórios
                            </label>
                            <p className="text-sm text-gray-600 mt-1">
                                O serviço requer recursos específicos (ex: Sala 1 + Maca 1 + Equipamento Laser). 
                                Todos os recursos selecionados devem estar disponíveis simultaneamente.
                            </p>
                            
                            {/* Seleção de Recursos Fixos */}
                            {!servico.usaPoolRecurso && (
                                <div className="mt-4 pl-6">
                                    <label className="text-sm font-medium text-gray-700">Selecione os recursos obrigatórios:</label>
                                    <div className="checkbox-group max-h-[300px] overflow-y-auto border border-gray-200 p-3 rounded-lg bg-white mt-2">
                                        {isLoadingRecursos ? (
                                            <div className="flex items-center text-sm text-gray-500 p-4 justify-center">
                                                <Loader2 size={16} className="animate-spin mr-2" /> Carregando recursos...
                                            </div>
                                        ) : recursos.length > 0 ? (
                                            recursos.map(r => {
                                                const rId = extractId(r);
                                                const isSelecionado = servico.recursosObrigatorios.includes(rId);
                                                return (
                                                    <label key={rId} className={`flex items-center justify-between p-3 mb-2 rounded-lg border cursor-pointer transition-all ${isSelecionado ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                                            <input type="checkbox" checked={isSelecionado} onChange={(e) => {
                                                                const newList = e.target.checked 
                                                                    ? [...servico.recursosObrigatorios, rId]
                                                                    : servico.recursosObrigatorios.filter(id => id !== rId);
                                                                setServico(prev => ({ ...prev, recursosObrigatorios: newList }));
                                                            }} className="form-checkbox" />
                                                            <Cpu size={18} className={isSelecionado ? "text-blue-500" : "text-gray-400"} />
                                                            <span className={`font-medium text-sm ${isSelecionado ? 'text-blue-900' : 'text-gray-700'}`}>{r.nome}</span>
                                                        </div>
                                                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{r.tipo}</span>
                                                    </label>
                                                );
                                            })
                                        ) : (
                                            <p className="text-center text-gray-500 py-4">Nenhum recurso disponível</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Opção 2: Pool de Recursos */}
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-white border border-gray-200">
                        <input 
                            type="radio" 
                            id="estrategia-pool" 
                            name="usaPoolRecurso" 
                            value={true} 
                            checked={servico.usaPoolRecurso} 
                            onChange={(e) => {
                                setServico(prev => ({ ...prev, usaPoolRecurso: true, recursosObrigatorios: [] }));
                            }} 
                            className="form-radio mt-1"
                        />
                        <div className="flex-1">
                            <label htmlFor="estrategia-pool" className="font-medium flex items-center gap-2">
                                <Layers size={16} className="text-purple-600" /> Pool de Recursos (Barbearia)
                            </label>
                            <p className="text-sm text-gray-600 mt-1">
                                O serviço pode ser executado em QUALQUER recurso de um pool (ex: qualquer cadeira da barbearia). 
                                O sistema aloca automaticamente um recurso disponível.
                            </p>
                            
                            {/* Seleção do Pool */}
                            {servico.usaPoolRecurso && (
                                <div className="mt-4 pl-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="form-group">
                                            <label className="text-sm font-medium text-gray-700">Selecione o Pool de Recursos:</label>
                                            <select 
                                                value={servico.poolRecursoId} 
                                                onChange={(e) => setServico(prev => ({ ...prev, poolRecursoId: e.target.value }))}
                                                className={`form-input mt-1 ${getFieldError('poolRecursoId') ? 'border-red-500' : ''}`}
                                                disabled={isLoadingPools}
                                            >
                                                <option value="">Selecione um pool</option>
                                                {pools.map(pool => (
                                                    <option key={extractId(pool)} value={extractId(pool)}>
                                                        {pool.nome} ({pool.tipo}) - {pool.quantidade} recursos
                                                    </option>
                                                ))}
                                            </select>
                                            {getFieldError('poolRecursoId') && <p className="text-red-500 text-xs mt-1">{getFieldError('poolRecursoId')}</p>}
                                        </div>
                                        
                                        <div className="form-group">
                                            <label className="text-sm font-medium text-gray-700">Quantidade necessária:</label>
                                            <input 
                                                type="number" 
                                                name="quantidadeRecursosNecessaria" 
                                                value={servico.quantidadeRecursosNecessaria} 
                                                onChange={onChange} 
                                                className="form-input mt-1" 
                                                min="1" 
                                                max="10"
                                            />
                                            <p className="form-hint text-xs">Número de recursos do pool necessários (ex: 2 cadeiras)</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderImagemPrincipal = () => {
        const imagemUrl = servico.imagemPrincipal?.url || servico.imagemUrl;
        return (
            <div className="form-group full-width">
                <label className="form-section-title">Imagem Principal</label>
                <div className="flex flex-col md:flex-row gap-6 items-start">
                    <div className="flex flex-col items-center">
                        <div className="w-32 h-32 rounded-lg border-2 border-dashed border-gray-300 overflow-hidden bg-gray-50 flex items-center justify-center">
                            <img src={imagemUrl || DEFAULT_PLACEHOLDER_IMAGE} alt="Imagem Principal" className="w-full h-full object-cover" onError={(e) => { e.target.src = DEFAULT_PLACEHOLDER_IMAGE; }} />
                        </div>
                        <p className="text-xs text-gray-500 mt-2 text-center">{imagemUrl ? 'Imagem atual' : 'Sem imagem'}</p>
                    </div>
                    <div className="flex-1">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm text-blue-700">
                                {isEditing ? (
                                    <>Para gerenciar imagens, use o botão <strong>"Gerenciar Imagens"</strong> na lista de serviços.</>
                                ) : (
                                    <>Após criar o serviço, você poderá adicionar e gerenciar imagens através do botão <strong>"Gerenciar Imagens"</strong> na lista de serviços.</>
                                )}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderResumoDuracao = () => (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-blue-800 mb-2 flex items-center"><Clock size={16} className="mr-2" />Resumo de Duração</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><span className="text-blue-600 font-medium">Serviço:</span><p>{servico.duracaoSessaoEmMinutos || 0} min</p></div>
                <div><span className="text-blue-600 font-medium">Preparação:</span><p>{servico.metadata.tempoPreparacao || 0} min</p></div>
                <div><span className="text-blue-600 font-medium">Limpeza:</span><p>{servico.metadata.tempoLimpeza || 0} min</p></div>
                {isServicoExterno && <div><span className="text-blue-600 font-medium">Deslocamento:</span><p>{servico.metadata.tempoDeslocamento || 0} min</p></div>}
                <div className="col-span-2 md:col-span-4 border-t pt-2"><span className="text-blue-800 font-bold">Duração Total:</span><p className="text-blue-800 font-bold">{calcularDuracaoTotal()} min</p></div>
            </div>
        </div>
    );

    const renderPacotesPreco = () => (
        <div className="form-group full-width">
            <label className="flex items-center gap-2"><Package size={16} /> Pacotes de Preço</label>
            {errors.pacotes && <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4"><p className="text-red-700 text-sm font-medium">{errors.pacotes}</p></div>}
            <div className="space-y-2">
                {servico.pacotesDePreco.length === 0 && <p className="text-sm text-gray-500">Nenhum pacote adicionado.</p>}
                {servico.pacotesDePreco.map((pacote, index) => (
                    <div key={index} className={`p-4 border rounded-lg mb-3 transition-colors ${pacote.ativo ? 'bg-white border-gray-200 shadow-sm' : 'bg-gray-50 border-dashed'}`}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
                            <div className="sm:col-span-2 lg:col-span-2">
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Nome*</label>
                                <input type="text" placeholder="Nome do Pacote" value={pacote.nome} onChange={(e) => handlePacoteChange(index, 'nome', e.target.value)} className={`form-input mt-1 ${getFieldError(`pacote-${index}-nome`) ? 'border-red-500' : ''}`} required />
                                {getFieldError(`pacote-${index}-nome`) && <p className="text-red-500 text-xs mt-1">{getFieldError(`pacote-${index}-nome`)}</p>}
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Sessões*</label>
                                <input type="number" placeholder="Sessões" value={pacote.sessoes} onChange={(e) => handlePacoteChange(index, 'sessoes', e.target.value)} className={`form-input mt-1 ${getFieldError(`pacote-${index}-sessoes`) ? 'border-red-500' : ''}`} min="1" required />
                                {getFieldError(`pacote-${index}-sessoes`) && <p className="text-red-500 text-xs mt-1">{getFieldError(`pacote-${index}-sessoes`)}</p>}
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Valor ({currencySymbol})*</label>
                                <input type="number" placeholder="Valor" value={pacote.valor} onChange={(e) => handlePacoteChange(index, 'valor', e.target.value)} className={`form-input mt-1 ${getFieldError(`pacote-${index}-valor`) ? 'border-red-500' : ''}`} min="0" step="0.01" required />
                                {getFieldError(`pacote-${index}-valor`) && <p className="text-red-500 text-xs mt-1">{getFieldError(`pacote-${index}-valor`)}</p>}
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Desconto %</label>
                                <input type="number" placeholder="0" value={pacote.descontoPercentual} onChange={(e) => handlePacoteChange(index, 'descontoPercentual', e.target.value)} className={`form-input mt-1 ${getFieldError(`pacote-${index}-desconto`) ? 'border-red-500' : ''}`} min="0" max="100" />
                                {getFieldError(`pacote-${index}-desconto`) && <p className="text-red-500 text-xs mt-1">{getFieldError(`pacote-${index}-desconto`)}</p>}
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Validade (meses)</label>
                                <input type="number" placeholder="12" value={pacote.validadeMeses} onChange={(e) => handlePacoteChange(index, 'validadeMeses', e.target.value)} className={`form-input mt-1 ${getFieldError(`pacote-${index}-validade`) ? 'border-red-500' : ''}`} min="1" max="36" />
                                {getFieldError(`pacote-${index}-validade`) && <p className="text-red-500 text-xs mt-1">{getFieldError(`pacote-${index}-validade`)}</p>}
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-gray-100">
                            <button type="button" onClick={() => togglePacoteAtivo(index)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${pacote.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>{pacote.ativo ? 'Pacote Ativo' : 'Pacote Inativo'}</button>
                            <button type="button" onClick={() => handleRemovePacote(index)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-full transition-colors" title="Remover Pacote"><Trash2 size={16} /></button>
                        </div>
                    </div>
                ))}
            </div>
            <button type="button" onClick={handleAddPacote} className="mt-2 action-button action-button-secondary inline-flex items-center"><PlusCircle size={18} className="icon"/> Adicionar Pacote</button>
        </div>
    );

    const renderUnidades = () => {
        if (isLoadingUnidades) {
            return (
                <div className="flex items-center justify-center p-8">
                    <Loader2 size={24} className="animate-spin text-blue-500 mr-2" />
                    <span>Carregando unidades...</span>
                </div>
            );
        }

        if (isErrorUnidades) {
            return (
                <div className="alert alert-danger">
                    <p>Erro ao carregar unidades. Tente novamente.</p>
                </div>
            );
        }

        if (unidades.length === 0) {
            return (
                <div className="alert alert-warning">
                    <p>Nenhuma unidade disponível para vincular.</p>
                    <p className="text-sm mt-2">Você precisa criar unidades primeiro para poder vinculá-las aos serviços.</p>
                </div>
            );
        }

        return (
            <div className="form-group full-width">
                <label className="form-group-title mb-2">Unidades Disponíveis</label>
                <p className="form-hint mb-4">
                    Selecione as unidades da sua conta onde este serviço estará disponível para agendamento.
                </p>
                
                <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                    <div className="divide-y divide-gray-100">
                        {unidades.map(unidade => {
                            const unitId = extractId(unidade);
                            const isVinculada = isUnidadeVinculada(unitId);
                            const detalhes = getUnidadeDetalhes(unitId);
                            const isExpanded = expandedUnidades[unitId];

                            return (
                                <div key={unitId} className={`bg-white transition-colors ${isVinculada ? 'bg-blue-50/30' : ''}`}>
                                    <div 
                                        className={`flex items-center justify-between p-4 transition-colors cursor-pointer ${isVinculada ? '' : 'opacity-70 hover:opacity-100'}`}
                                        onClick={() => onUnidadeChange(unitId, !isVinculada)}
                                    >
                                        <div className="flex items-center gap-4 flex-1">
                                            <button 
                                                type="button" 
                                                className={`relative inline-flex items-center h-[22px] w-10 rounded-full transition-colors focus:outline-none flex-shrink-0 ${isVinculada ? 'bg-amber-400' : 'bg-gray-200'}`}
                                                onClick={(e) => { e.stopPropagation(); onUnidadeChange(unitId, !isVinculada); }}
                                            >
                                                <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform shadow-sm ${isVinculada ? 'translate-x-[22px]' : 'translate-x-1'}`}></span>
                                            </button>
                                            <div className="flex-1 min-w-0">
                                                <span className="text-[15px] font-medium text-gray-800 block truncate">{unidade.nome}</span>
                                            </div>
                                        </div>
                                        <div 
                                            className={`flex items-center gap-3 ${isVinculada ? 'group' : 'pointer-events-none'}`}
                                            onClick={(e) => { e.stopPropagation(); if (isVinculada) toggleUnidadeExpansion(unitId); }}
                                        >
                                            <span className="text-xs text-gray-400 hidden sm:block">
                                                {isVinculada && (detalhes?.precoPersonalizado ? 'Preço personalizado' : 'Configuração Padrão')}
                                            </span>
                                            {isVinculada && (
                                                <ChevronDown size={18} className={`text-gray-400 transition-transform group-hover:text-gray-600 ${isExpanded ? 'rotate-180' : ''}`} />
                                            )}
                                        </div>
                                    </div>

                                    {isExpanded && isVinculada && (
                                        <div className="p-5 bg-gray-50/50 border-t border-gray-100">
                                            <h5 className="font-medium text-sm mb-4 text-gray-700">Configurações para esta unidade</h5>
                                            
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                                                <div className="form-group mb-0">
                                                    <label className="text-sm font-medium">Preço Personalizado ({currencySymbol})</label>
                                                    <input 
                                                        type="number" 
                                                        value={detalhes.precoPersonalizado || ''} 
                                                        onChange={(e) => onUnidadeConfigChange(unitId, 'precoPersonalizado', e.target.value)} 
                                                        className="form-input text-sm mt-1 bg-white" 
                                                        placeholder="Deixe em branco para preço padrão" 
                                                        step="0.01" min="0" 
                                                    />
                                                </div>
                                                
                                                <div className="flex flex-col gap-3 justify-center pt-2">
                                                    <label className="checkbox-label text-sm flex items-center cursor-pointer">
                                                        <div className={`relative inline-flex items-center h-[22px] w-10 rounded-full transition-colors mr-3 ${detalhes.ativo !== false ? 'bg-green-500' : 'bg-gray-300'}`}>
                                                            <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform shadow-sm ${detalhes.ativo !== false ? 'translate-x-[22px]' : 'translate-x-1'}`} />
                                                        </div>
                                                        <input 
                                                            type="checkbox" 
                                                            checked={detalhes.ativo !== false} 
                                                            onChange={(e) => onUnidadeConfigChange(unitId, 'ativo', e.target.checked)} 
                                                            className="hidden"
                                                        />
                                                        <span className="font-medium text-gray-800">Ativo nesta unidade</span>
                                                    </label>

                                                    <label className="checkbox-label text-sm flex items-center cursor-pointer">
                                                        <div className={`relative inline-flex items-center h-[22px] w-10 rounded-full transition-colors mr-3 ${detalhes.disponivelOnline || false ? 'bg-blue-500' : 'bg-gray-300'}`}>
                                                            <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform shadow-sm ${detalhes.disponivelOnline || false ? 'translate-x-[22px]' : 'translate-x-1'}`} />
                                                        </div>
                                                        <input 
                                                            type="checkbox" 
                                                            checked={detalhes.disponivelOnline || false} 
                                                            onChange={(e) => onUnidadeConfigChange(unitId, 'disponivelOnline', e.target.checked)} 
                                                            className="hidden"
                                                        />
                                                        <span className="font-medium text-gray-800">Disponível para agendamento online</span>
                                                    </label>
                                                    <p className="text-xs text-gray-500">Restringe só ESTA unidade. Se desmarcado aqui, o serviço não aparece pra agendamento online quando o cliente escolhe esta unidade — mesmo que a chave geral (aba Configurações) esteja marcada.</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    const renderVinculos = () => {
        return (
            <div className="form-grid-container">
                {/* ✅ NOVA SEÇÃO: Pool de Recursos */}
                {renderSeçãoPoolRecursos()}
            </div>
        );
    };

    const renderAbaBasicos = () => (
        <fieldset className="form-section border-0 p-0 m-0">
            <legend className="form-section-title sr-only">Dados do Serviço</legend>
            <div className="form-grid-container">
                <div className="form-group">
                    <label htmlFor="nome">Nome do Serviço*</label>
                    <input type="text" id="nome" name="nome" value={servico.nome} onChange={onChange} required className={`form-input ${errors.nome ? 'border-red-500' : ''}`} placeholder="Ex: Limpeza de Pele, Depilação a Laser..." />
                    {errors.nome && <p className="text-red-500 text-xs mt-1">{errors.nome}</p>}
                </div>
                <div className="form-group">
                    <label htmlFor="status">Status</label>
                    <select name="status" id="status" value={servico.status} onChange={onChange} className="form-input">
                        <option value="Ativo">Ativo</option>
                        <option value="Inativo">Inativo</option>
                    </select>
                </div>
                <div className="form-group full-width">
                    <label htmlFor="descricao">Descrição</label>
                    <textarea id="descricao" name="descricao" value={servico.descricao} onChange={onChange} rows="3" className="form-input" placeholder="Descreva detalhadamente o serviço..."></textarea>
                </div>
                <div className="form-group">
                    <label htmlFor="categoria">Categoria*</label>
                    {renderCategoriaField()}
                </div>
                <div className="form-group full-width">
                    <label htmlFor="tags" className="form-label"><Tag size={16} className="inline mr-1" /> Tags</label>
                    <div className="flex flex-col sm:flex-row gap-2 mb-2">
                        <input type="text" value={novaTag} onChange={(e) => setNovaTag(e.target.value)} onKeyPress={handleTagKeyPress} className="form-input flex-1" placeholder="Adicionar tag..." />
                        <button type="button" onClick={adicionarTag} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2.5 sm:py-2 rounded-md flex items-center justify-center gap-1 transition-colors" disabled={!novaTag.trim()}>
                            <PlusCircle size={16} /> Adicionar
                        </button>
                    </div>
                    <p className="form-hint text-sm text-gray-600 mb-2">Pressione Enter ou clique no botão para adicionar Tags</p>
                    <div className="flex flex-wrap gap-2">
                        {servico.tags.map((tag, index) => (
                            <span key={index} className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                                {tag}
                                <button type="button" onClick={() => removerTag(tag)} className="text-green-600 hover:text-green-800 transition-colors ml-1"><Minus size={12} /></button>
                            </span>
                        ))}
                        {servico.tags.length === 0 && <span className="text-sm text-gray-500">Nenhuma tag adicionada</span>}
                    </div>
                </div>
                {renderImagemPrincipal()}
            </div>
        </fieldset>
    );

    const renderConfiguracoesAvancadas = () => (
        <div className="form-grid-container">
            <div className="form-group-checkbox">
                <input type="checkbox" id="agendavel" name="agendavel" checked={servico.agendavel} onChange={onChange} className="form-checkbox" />
                <label htmlFor="agendavel">Serviço agendável?</label>
                <p className="form-hint">
                    {isServicoExterno
                        ? 'Serviços de Uso Externo (a domicílio) precisam ser agendáveis — desmarcar aqui vai ser rejeitado ao salvar.'
                        : 'Se desmarcado, o serviço não ocupa horário de agenda — funciona como um item avulso (vendido sem marcar dia/hora). Exige duração definida quando marcado.'}
                </p>
            </div>
            {servico.agendavel && (
                <div className="form-group-checkbox">
                    <input type="checkbox" id="controlaSessao" name="controlaSessao" checked={servico.controlaSessao} onChange={onChange} className="form-checkbox" />
                    <label htmlFor="controlaSessao">Controla Sessão?</label>
                    <p className="form-hint">Para serviços com sessões recorrentes (ex.: depilação a laser). Mostra "Sessão N" no card do agendamento, contando os agendamentos anteriores desse cliente para este serviço — não define um total nem bloqueia o agendamento.</p>
                </div>
            )}
            <div className="form-group">
                <label className="form-group-title">Tipo de Serviço*</label>
                <div className="space-y-3 border p-3 rounded-md bg-gray-50">
                    <div className="flex items-start">
                        <input type="radio" id="tipo-interno" name="metadata.tipoServico" value="interno" checked={servico.metadata.tipoServico === 'interno'} onChange={onChange} className="form-radio mr-2 mt-1" />
                        <div>
                            <label htmlFor="tipo-interno" className="font-medium flex items-center"><Building size={16} className="inline mr-2 text-blue-600"/>Uso Interno</label>
                            <p className="text-sm text-gray-600 ml-0 mt-1">Serviço realizado dentro do estabelecimento</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <input type="radio" id="tipo-externo" name="metadata.tipoServico" value="externo" checked={servico.metadata.tipoServico === 'externo'} onChange={onChange} className="form-radio mr-2 mt-1" />
                        <div>
                            <label htmlFor="tipo-externo" className="font-medium flex items-center"><Globe size={16} className="inline mr-2 text-green-600"/>Uso Externo</label>
                            <p className="text-sm text-gray-600 ml-0 mt-1">Serviço realizado fora do estabelecimento, no local desejado pelo cliente</p>
                        </div>
                    </div>
                </div>
            </div>

            {isServicoExterno && (
                <div className="form-group full-width border border-green-200 rounded-lg p-4 bg-green-50">
                    <h4 className="font-medium text-green-800 mb-4 flex items-center"><MapPin size={16} className="mr-2" />Configurações para Serviço Externo</h4>
                    <div className="form-group-checkbox mb-4">
                        <input type="checkbox" id="requerDeslocamento" name="metadata.requerDeslocamento" checked={servico.metadata.requerDeslocamento} onChange={onChange} className="form-checkbox" />
                        <label htmlFor="requerDeslocamento" className="font-medium">Requer deslocamento do profissional?</label>
                        <p className="form-hint">Marque se o profissional precisa se deslocar até o local do cliente</p>
                    </div>
                    {servico.metadata.requerDeslocamento && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="form-group">
                                <label htmlFor="raioAtendimento" className="font-medium">Raio de Atendimento (km)*</label>
                                <input type="number" id="raioAtendimento" name="metadata.raioAtendimento" value={servico.metadata.raioAtendimento} onChange={onChange} className={`form-input ${getFieldError('raioAtendimento') ? 'border-red-500' : ''}`} min="1" placeholder="Ex: 10" required />
                                {getFieldError('raioAtendimento') && <p className="text-red-500 text-xs mt-1">{getFieldError('raioAtendimento')}</p>}
                                <p className="form-hint">Distância máxima para atendimento</p>
                            </div>
                            <div className="form-group">
                                <label htmlFor="tempoDeslocamento" className="font-medium">Tempo de Deslocamento (min)*</label>
                                <input type="number" id="tempoDeslocamento" name="metadata.tempoDeslocamento" value={servico.metadata.tempoDeslocamento} onChange={onChange} className={`form-input ${getFieldError('tempoDeslocamento') ? 'border-red-500' : ''}`} min="0" placeholder="Ex: 30" required />
                                {getFieldError('tempoDeslocamento') && <p className="text-red-500 text-xs mt-1">{getFieldError('tempoDeslocamento')}</p>}
                                <p className="form-hint">Tempo estimado de deslocamento</p>
                            </div>
                            <div className="form-group">
                                <label htmlFor="custoDeslocamento" className="font-medium">Custo de Deslocamento ({currencySymbol})*</label>
                                <input type="number" id="custoDeslocamento" name="metadata.custoDeslocamento" value={servico.metadata.custoDeslocamento} onChange={onChange} className={`form-input ${getFieldError('custoDeslocamento') ? 'border-red-500' : ''}`} min="0" step="0.01" placeholder="0.00" required />
                                {getFieldError('custoDeslocamento') && <p className="text-red-500 text-xs mt-1">{getFieldError('custoDeslocamento')}</p>}
                                <p className="form-hint">Custo adicional por deslocamento</p>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="form-group-checkbox">
                <input type="checkbox" id="disponivelOnline" name="metadata.disponivelOnline" checked={servico.metadata.disponivelOnline} onChange={onChange} className="form-checkbox" />
                <label htmlFor="disponivelOnline">Disponível para agendamento online? {hasUsoExterno && !servico.metadata.disponivelOnline && <span className="ml-2 text-orange-600 text-sm font-medium">(Recomendado)</span>}</label>
                <p className="form-hint">Se desmarcado, clientes não conseguem agendar este serviço sozinhos pelo site/app em nenhuma unidade — só a equipe consegue agendar por dentro do painel. Vale pra conta toda; pode ser restrito só numa unidade específica na aba Unidades.</p>
            </div>

            <div className="form-group full-width">
                <label className="form-group-title">Tempos Adicionais (minutos)</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                        <label htmlFor="tempoPreparacao">Tempo de Preparação</label>
                        <input type="number" id="tempoPreparacao" name="metadata.tempoPreparacao" value={servico.metadata.tempoPreparacao} onChange={onChange} className="form-input" min="0" placeholder="0" />
                        <p className="form-hint">Tempo antes do serviço para preparação</p>
                    </div>
                    <div className="form-group">
                        <label htmlFor="tempoLimpeza">Tempo de Limpeza</label>
                        <input type="number" id="tempoLimpeza" name="metadata.tempoLimpeza" value={servico.metadata.tempoLimpeza} onChange={onChange} className="form-input" min="0" placeholder="0" />
                        <p className="form-hint">Tempo após o serviço para limpeza</p>
                    </div>
                </div>
            </div>

            <div className="form-group-checkbox">
                <input type="checkbox" id="visivelParaCliente" name="visivelParaCliente" checked={servico.visivelParaCliente} onChange={onChange} className="form-checkbox" />
                <label htmlFor="visivelParaCliente">Visível para o cliente no site/app?</label>
                <p className="form-hint">Se desmarcado, o serviço fica totalmente oculto do cliente — não aparece na loja nem no agendamento online, em nenhuma unidade. Diferente de "Disponível para agendamento online", que só afeta se dá pra agendar, não se aparece.</p>
            </div>

            <div className="form-group-checkbox">
                <input type="checkbox" id="requerAnamnese" name="requerAnamnese" checked={servico.requerAnamnese} onChange={onChange} className="form-checkbox" />
                <label htmlFor="requerAnamnese">Requer Anamnese?</label>
            </div>

            {servico.requerAnamnese && (
                <div className="form-group full-width">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-group">
                            <label htmlFor="frequenciaAnamnese">Frequência da Anamnese</label>
                            <select id="frequenciaAnamnese" name="configAnamnese.frequencia" value={servico.configAnamnese.frequencia} onChange={onChange} className="form-input">
                                {['Não Repetir', 'A cada semana', 'A cada duas semanas', 'A cada mês', 'A cada 3 meses', 'A cada 6 meses', 'A cada ano'].map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="diasValidadeAnamnese">Validade (dias)</label>
                            <input type="number" id="diasValidadeAnamnese" name="configAnamnese.diasValidade" value={servico.configAnamnese.diasValidade} onChange={onChange} className="form-input" min="1" max="365" />
                        </div>
                    </div>
                </div>
            )}

            <div className="form-group full-width">
                <label htmlFor="observacoesInternas" className="font-medium">Observações Internas</label>
                <textarea id="observacoesInternas" name="metadata.observacoesInternas" value={servico.metadata.observacoesInternas} onChange={onChange} rows="3" className="form-input" placeholder="Informações internas sobre o serviço..."></textarea>
                <p className="form-hint">Estas observações são visíveis apenas para administradores e profissionais</p>
            </div>
        </div>
    );

    const onSubmit = async e => {
        e.preventDefault();
        setErrors({});

        try {
            const errosObrigatorios = validarCamposObrigatorios();
            const errosCategoria = validarCategoria();
            const errosPacotes = servico.tipoDePrecificacao === 'Por Pacote' ? validarPacotes() : {};
            const errosExternos = validarServicoExterno();

            const todosErros = { ...errosObrigatorios, ...errosCategoria, ...errosPacotes, ...errosExternos };

            if (Object.keys(todosErros).length > 0) {
                setErrors(todosErros);
                if (errosObrigatorios.nome || errosCategoria.categoria) goToTab('basicos');
                else if (Object.keys(errosPacotes).length > 0) goToTab('preco');
                else if (Object.keys(errosExternos).length > 0) goToTab('configuracoes');
                setAlert('Corrija os erros antes de salvar.', 'danger');
                return;
            }

            const servicoDataToSend = {
                ...servico,
                ativo: servico.status === 'Ativo',
                categoria: extractId(servico.categoria) || null,
                imagemUrl: servico.imagemUrl || null,
                profissionaisHabilitados: undefined,
                // ✅ NOVOS CAMPOS
                usaPoolRecurso: servico.usaPoolRecurso,
                poolRecursoId: servico.usaPoolRecurso ? extractId(servico.poolRecursoId) : null,
                quantidadeRecursosNecessaria: servico.quantidadeRecursosNecessaria || 1,
                recursosObrigatorios: servico.recursosObrigatorios || []
            };

            const precificacaoValida = await validarPrecificacao(servicoDataToSend);
            if (!precificacaoValida) {
                goToTab('preco');
                return;
            }

            if (servicoDataToSend.pacotesDePreco) {
                servicoDataToSend.pacotesDePreco = servicoDataToSend.pacotesDePreco.map(pacote => ({
                    nome: pacote.nome.trim(),
                    sessoes: Math.max(1, pacote.sessoes || 1),
                    valor: Math.max(0, pacote.valor || 0),
                    descontoPercentual: Math.min(100, Math.max(0, pacote.descontoPercentual || 0)),
                    validadeMeses: pacote.validadeMeses ? Math.min(36, Math.max(1, pacote.validadeMeses)) : 12,
                    ativo: pacote.ativo !== false,
                    ordem: pacote.ordem || 0,
                    descricao: pacote.descricao || ''
                })).filter(pacote => pacote.ativo);
            }

            if (servicoDataToSend.tipoDePrecificacao !== 'Fixo' && servicoDataToSend.tipoDePrecificacao !== 'A Partir de') {
                servicoDataToSend.valorFixo = undefined;
            }
            if (servicoDataToSend.tipoDePrecificacao === 'Sob Consulta') {
                servicoDataToSend.precoVisivelParaCliente = false;
            }
            delete servicoDataToSend.status;

            if (servicoDataToSend.duracaoSessaoEmMinutos) servicoDataToSend.duracaoSessaoEmMinutos = Number(servicoDataToSend.duracaoSessaoEmMinutos);
            if (servicoDataToSend.valorFixo !== undefined) servicoDataToSend.valorFixo = Number(servicoDataToSend.valorFixo);

            if (servicoDataToSend.metadata) {
                servicoDataToSend.metadata.tempoPreparacao = Number(servicoDataToSend.metadata.tempoPreparacao) || 0;
                servicoDataToSend.metadata.tempoLimpeza = Number(servicoDataToSend.metadata.tempoLimpeza) || 0;
                servicoDataToSend.metadata.raioAtendimento = Number(servicoDataToSend.metadata.raioAtendimento) || 0;
                servicoDataToSend.metadata.tempoDeslocamento = Number(servicoDataToSend.metadata.tempoDeslocamento) || 0;
                servicoDataToSend.metadata.custoDeslocamento = Number(servicoDataToSend.metadata.custoDeslocamento) || 0;
            }

            const mutation = isEditing 
                ? updateServico.mutateAsync({ id: current._id, servicoData: servicoDataToSend }) 
                : addServico.mutateAsync(servicoDataToSend);

            const response = await mutation;
            
            setAlert("Serviço salvo com sucesso!", "success");

            if (!isEditing && onCreateSuccess) onCreateSuccess(response.data.data);
            onCloseForm();

        } catch (error) {
            if (error.response?.data?.error?.includes('categoria') || 
                error.response?.data?.error?.includes('ObjectId') || 
                error.response?.data?.error?.includes('CastError')) {
                setErrors({ categoria: 'Erro na categoria selecionada. Por favor, selecione novamente.' });
                goToTab('basicos');
                setAlert('Erro na categoria. Por favor, selecione uma categoria válida.', 'danger');
            } else {
                setAlert(error.response?.data?.error || "Erro ao salvar serviço", "danger");
            }
        }
    };

    const tabsErrors = {
        basicos: !!(errors.nome || errors.categoria),
        preco: !!(errors.duracaoSessaoEmMinutos || errors.pacotes),
        unidades: false,
        vinculos: !!(errors.poolRecursoId || errors.recursosObrigatorios),
        configuracoes: !!(errors.raioAtendimento || errors.tempoDeslocamento || errors.custoDeslocamento)
    };

    return (
        <form onSubmit={onSubmit} noValidate>
            <div className="form-header">
                <h3 className="form-title">
                    {isEditing && servico.nome ? `Editando: ${servico.nome}` : 'Novo Serviço'}
                </h3>
                <button
                    type="button"
                    onClick={onCloseForm}
                    title="Fechar Formulário"
                    className="form-close-button"
                >
                    <XCircle size={24} />
                </button>
            </div>

            <FormStepper currentStep={currentStep} steps={STEP_LABELS} stepErrors={STEP_IDS.map((id) => tabsErrors[id])} />

            <div className="form-tabs-content">
                {STEP_IDS[currentStep - 1] === 'basicos' && renderAbaBasicos()}
                {STEP_IDS[currentStep - 1] === 'preco' && (
                    <fieldset className="form-section border-0 p-0 m-0">
                        <legend className="form-section-title sr-only">Preço e Duração</legend>
                        {renderResumoDuracao()}
                        <div className="form-grid-container">
                            <div className="form-group">
                                <label htmlFor="duracaoSessaoEmMinutos">Duração do Serviço (min)*</label>
                                <input type="number" id="duracaoSessaoEmMinutos" name="duracaoSessaoEmMinutos" value={servico.duracaoSessaoEmMinutos} onChange={onChange} className={`form-input ${errors.duracaoSessaoEmMinutos ? 'border-red-500' : ''}`} min="5" step="5" required={servico.agendavel} />
                                {errors.duracaoSessaoEmMinutos && <p className="text-red-500 text-xs mt-1">{errors.duracaoSessaoEmMinutos}</p>}
                            </div>
                            <div className="form-group">
                                <label htmlFor="tipoDePrecificacao">Tipo de Precificação</label>
                                <select id="tipoDePrecificacao" name="tipoDePrecificacao" value={servico.tipoDePrecificacao} onChange={onChange} className="form-input">
                                    <option value="Fixo">Fixo</option>
                                    <option value="Por Pacote">Por Pacote</option>
                                    <option value="Sob Consulta">Sob Consulta</option>
                                    <option value="A Partir de">A Partir de</option>
                                </select>
                            </div>
                            {(servico.tipoDePrecificacao === 'Fixo' || servico.tipoDePrecificacao === 'A Partir de') && (
                                <div className="form-group">
                                    <label htmlFor="valorFixo">{servico.tipoDePrecificacao === 'A Partir de' ? `Valor a Partir de (${currencySymbol})` : `Valor Fixo (${currencySymbol})`} {isServicoExterno && servico.metadata.custoDeslocamento > 0 && <span className="text-orange-600 text-sm ml-2">+ {formatCurrency(servico.metadata.custoDeslocamento, currency, locale)} de deslocamento</span>}</label>
                                    <input type="number" id="valorFixo" name="valorFixo" value={servico.valorFixo} onChange={onChange} className="form-input" min="0" step="0.01" placeholder="0.00" required />
                                </div>
                            )}
                            {servico.tipoDePrecificacao === 'Por Pacote' && renderPacotesPreco()}
                            {servico.tipoDePrecificacao !== 'Sob Consulta' && (
                                <div className="form-group-checkbox full-width">
                                    <input type="checkbox" id="precoVisivelParaCliente" name="precoVisivelParaCliente" checked={servico.precoVisivelParaCliente} onChange={onChange} className="form-checkbox" />
                                    <label htmlFor="precoVisivelParaCliente">Preço visível para o cliente?</label>
                                </div>
                            )}
                        </div>
                    </fieldset>
                )}
                {STEP_IDS[currentStep - 1] === 'unidades' && (
                    <fieldset className="form-section border-0 p-0 m-0">
                        <legend className="form-section-title sr-only">Unidades Disponíveis</legend>
                        <div className="form-grid-container">{renderUnidades()}</div>
                    </fieldset>
                )}
                {STEP_IDS[currentStep - 1] === 'vinculos' && (
                    <fieldset className="form-section border-0 p-0 m-0">
                        <legend className="form-section-title sr-only">Vínculos de Recursos</legend>
                        {renderVinculos()}
                    </fieldset>
                )}
                {STEP_IDS[currentStep - 1] === 'configuracoes' && (
                    <fieldset className="form-section border-0 p-0 m-0">
                        <legend className="form-section-title sr-only">Configurações Avançadas</legend>
                        {renderConfiguracoesAvancadas()}
                    </fieldset>
                )}
            </div>

            <div className="form-actions">
                <button type="button" onClick={onCloseForm} className="action-button action-button-secondary" disabled={loading || validating}><XCircle size={18} className="icon"/> Cancelar</button>
                <FormStepNavigation
                    currentStep={currentStep}
                    totalSteps={STEP_LABELS.length}
                    onBack={() => setCurrentStep((s) => s - 1)}
                    onNext={() => setCurrentStep((s) => s + 1)}
                    isSubmitting={loading || validating}
                    submitLabel={validating ? 'Validando...' : (isEditing ? 'Atualizar Serviço' : 'Salvar Serviço')}
                />
            </div>
        </form>
    );
};

ServicoForm.propTypes = {
    onCloseForm: PropTypes.func.isRequired,
    current: PropTypes.object,
    accountId: PropTypes.string,
    recursos: PropTypes.array,
    pools: PropTypes.array,
    unidades: PropTypes.array,
    categorias: PropTypes.array,
    isLoadingRecursos: PropTypes.bool,
    isErrorRecursos: PropTypes.bool,
    isLoadingPools: PropTypes.bool,
    isErrorPools: PropTypes.bool,
    isLoadingUnidades: PropTypes.bool,
    isErrorUnidades: PropTypes.bool,
    isLoadingCategorias: PropTypes.bool,
    isErrorCategorias: PropTypes.bool,
    onCreateSuccess: PropTypes.func,
    mode: PropTypes.oneOf(['create', 'edit']),
};

export default ServicoForm;
